/**
 * ClientWorkDetails.tsx
 *
 * File-level:
 * - Renders a detailed portfolio page for a single client.
 * - Shows client info, original media (videos/images), uploaded media and upload controls.
 * - Persists uploaded media to localStorage per-client.
 *
 * Note:
 * - Admin embed feature removed per request: embed state, admin embed UI and persistence were eliminated.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router'
import { Button } from '../components/ui/button'
import { ArrowLeft, Upload, Video, Image as ImageIcon, X, CheckCircle, AlertCircle } from 'lucide-react'
import { clientsData, Client } from '../data/clientsData'
import OriginalImagesManager from '../components/original/OriginalImagesManager'
import { uploadFileToCdn } from '../utils/uploadToCdnClient'
import { socialLinkOverrides } from '../data/socialLinkOverrides'
import InstagramPair from '../components/embeds/InstagramPair'

/**
 * ClientWorkDetails
 *
 * Component-level:
 * - Displays all client details including services, original media and uploaded media.
 * - Handles local file uploads (images/videos) and admin-only upload UI.
 *
 * Responsibilities:
 * - Load client metadata from local dataset.
 * - Load and persist uploaded images/videos to localStorage.
 * - Try to upload files to CDN using /api/upload-url; fall back to object URL on failure.
 */
export default function ClientWorkDetails(): JSX.Element {
  const { clientId } = useParams<{ clientId?: string }>()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)

  const [client, setClient] = useState<Client | null>(null)
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [uploadedVideos, setUploadedVideos] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle')

  /**
   * Admin detection (client-side).
   * - ?admin=true in URL OR localStorage key 'ed_admin_mode' === 'true' toggles admin UI.
   */
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get('admin') === 'true') {
        setIsAdmin(true)
        return
      }
      const stored = localStorage.getItem('ed_admin_mode')
      setIsAdmin(stored === 'true')
    } catch (err) {
      setIsAdmin(false)
    }
  }, [])

  /**
   * Initialize: find client entry and load persisted media for that client.
   * - Note: admin embed loading/persistence removed.
   */
  useEffect(() => {
    if (!clientId) return
    const foundClient = clientsData.find((c) => c.id === clientId)
    if (foundClient) {
      setClient(foundClient)

      const savedImages = localStorage.getItem(`client_${clientId}_images`)
      const savedVideos = localStorage.getItem(`client_${clientId}_videos`)

      if (savedImages) {
        try {
          setUploadedImages(JSON.parse(savedImages))
        } catch (err) {
          console.error('Failed to parse saved images:', err)
        }
      }

      if (savedVideos) {
        try {
          setUploadedVideos(JSON.parse(savedVideos))
        } catch (err) {
          console.error('Failed to parse saved videos:', err)
        }
      }
    } else {
      setClient(null)
    }
  }, [clientId])

  /**
   * handleImageUpload
   *
   * Create object URLs for uploaded images and persist them in localStorage.
   * Enhanced: Try uploading to CDN using uploadFileToCdn helper; on failure fall back to object URL.
   */
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    setUploadStatus('idle')

    try {
      const newImages: string[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('image/')) continue

        try {
          // Try uploading to CDN (requires backend /api/upload-url)
          // If successful, returns a public CDN URL
          // If backend is not available or upload fails, an error will be thrown and we fallback below
          // Pass clientId for backend metadata purposes
          // eslint-disable-next-line no-await-in-loop
          const publicUrl = await uploadFileToCdn(file, clientId)
          newImages.push(publicUrl)
          console.log('Uploaded image to CDN:', publicUrl)
        } catch (err) {
          // Fallback: object URL (temporary)
          console.warn('CDN upload failed, falling back to object URL:', err)
          const localUrl = URL.createObjectURL(file)
          newImages.push(localUrl)
        }
      }

      const updated = [...uploadedImages, ...newImages]
      setUploadedImages(updated)
      if (clientId) localStorage.setItem(`client_${clientId}_images`, JSON.stringify(updated))

      setUploadStatus('success')
      setTimeout(() => setUploadStatus('idle'), 2500)
    } catch (err) {
      console.error('Image upload error:', err)
      setUploadStatus('error')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  /**
   * handleVideoUpload
   *
   * Create object URLs for uploaded videos and persist them in localStorage.
   * Enhanced: Try uploading to CDN using uploadFileToCdn helper; on failure fall back to object URL.
   */
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    setUploadStatus('idle')

    try {
      const newVideos: string[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('video/')) continue

        try {
          // Try upload video to CDN
          // Note: Ensure backend supports larger video uploads and proper content-type
          // eslint-disable-next-line no-await-in-loop
          const publicUrl = await uploadFileToCdn(file, clientId)
          newVideos.push(publicUrl)
          console.log('Uploaded video to CDN:', publicUrl)
        } catch (err) {
          console.warn('CDN video upload failed, falling back to object URL:', err)
          const localUrl = URL.createObjectURL(file)
          newVideos.push(localUrl)
        }
      }

      const updated = [...uploadedVideos, ...newVideos]
      setUploadedVideos(updated)
      if (clientId) localStorage.setItem(`client_${clientId}_videos`, JSON.stringify(updated))

      setUploadStatus('success')
      setTimeout(() => setUploadStatus('idle'), 2500)
    } catch (err) {
      console.error('Video upload error:', err)
      setUploadStatus('error')
    } finally {
      setIsUploading(false)
      if (videoInputRef.current) videoInputRef.current.value = ''
    }
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Client Not Found</h2>
          <Button asChild variant="outline" className="bg-transparent">
            <Link to="/portfolio">Back to Portfolio</Link>
          </Button>
        </div>
      </div>
    )
  }

  /**
   * sortedOriginalVideos
   *
   * Ensure YouTube embeds come before Instagram embeds in the visual order.
   */
  const allVideos = client.mediaLinks.videos ?? []
  const sortedOriginalVideos = allVideos.slice().sort((a, b) => {
    const aYouTube = a.includes('youtube.com') || a.includes('youtu.be')
    const bYouTube = b.includes('youtube.com') || b.includes('youtu.be')
    if (aYouTube === bYouTube) return 0
    return aYouTube ? -1 : 1
  })

  const firstInstagramIndex = sortedOriginalVideos.findIndex((v) => v.includes('instagram.com'))

  /**
   * mergedSocialLinks
   *
   * Merge client.socialLinks with socialLinkOverrides for display.
   * - Overrides (if provided) take precedence over the original client.socialLinks.
   */
  const mergedSocialLinks = {
    ...(client.socialLinks ?? {}),
    ...(socialLinkOverrides[client.id] ?? {})
  }

  /**
   * renderVideoElements
   *
   * Build an array of JSX elements for original videos with special handling:
   * - Instagram reels render as fixed 350x630 iframes.
   * - Consecutive Instagram embeds are grouped into pairs using InstagramPair component.
   * - Insert an "Instagram" separator before the first Instagram embed.
   *
   * NOTE:
   * - User requested an additional Instagram embed to be shown alongside a specific reel.
   * - If the current single Instagram reel matches the known reel id, we append the provided post embed
   *   so both appear together (keeps grouping predictable and avoids script injection).
   */
  /**
   * renderVideoElements
   *
   * Purpose:
   * - Build an array of JSX elements for original videos with special handling for Instagram embeds.
   * - Special replacement: when a specific two-post pair (khandool C9u6F5IsWc_ + sumaya_goran C8r_ZDooB_T)
   *   appears consecutively, replace them with a single reel embed:
   *   - embed: https://www.instagram.com/reel/C2p6xKpLv2Q/embed
   *   - original: https://www.instagram.com/reel/C2p6xKpLv2Q/
   *
   * Rationale:
   * - This ensures the undesired two-column pair is removed and replaced by the requested single reel,
   *   while preserving all other rendering behavior.
   */
  /**
   * renderVideoElements
   *
   * - Build an array of JSX elements for original videos with special handling for Instagram embeds.
   * - Ensure that for clients which require a curated Ona pair, the two Instagram iframes
   *   are injected directly into the videos grid as individual iframe items so they appear
   *   alongside other video iframes.
   */
  const renderVideoElements = (): JSX.Element[] => {
    const elements: JSX.Element[] = []

    // Curated Ona pair (explicit embed urls). We'll inject these if the client's original
    // videos don't already include them so they appear in the Videos grid.
    const onaPair: [string, string] = [
      'https://www.instagram.com/reel/DSANxKviGcM/embed/',
      'https://www.instagram.com/reel/DQJ0wcIiIZi/embed/'
    ]

    for (let i = 0; i < sortedOriginalVideos.length; i++) {
      const video = sortedOriginalVideos[i]
      const isInstagram = video.includes('instagram.com')

      // Insert separator before first Instagram block
      if (i === firstInstagramIndex && firstInstagramIndex !== -1) {
        elements.push(
          <div key={`sep-${i}`} className="col-span-full flex items-center my-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="px-3 text-sm text-gray-500">Instagram</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        )
      }

      if (isInstagram) {
        const next = sortedOriginalVideos[i + 1]

        // Handle consecutive Instagram embeds
        if (next && next.includes('instagram.com')) {
          // Special-case replacement: when pair matches the two known posts, replace with single reel embed
          const a = video
          const b = next
          const isSpecialPair =
            (a.includes('khandool/p/C9u6F5IsWc_') && b.includes('sumaya_goran/p/C8r_ZDooB_T')) ||
            (a.includes('sumaya_goran/p/C8r_ZDooB_T') && b.includes('khandool/p/C9u6F5IsWc_'))

          if (isSpecialPair) {
            const replacementEmbed = 'https://www.instagram.com/reel/C2p6xKpLv2Q/embed/'
            const extraEmbed = 'https://www.instagram.com/reel/DA6Nd-yIIeZ/embed/'
            // Render the requested reel pair using Evy Baby iframe sizing/styling (live iframes)
            elements.push(
              <div key={`pair-replaced-${i}`} className="col-span-full flex justify-center">
                <InstagramPair
                  srcs={[replacementEmbed, extraEmbed]}
                  titles={[`${client.name} video ${i + 1}`, `${client.name} video ${i + 2}`]}
                  forceFallback={false}
                />
              </div>
            )
            i++ // skip the next that we replaced
            continue
          }

          // Default: render the pair side-by-side
          // Special-case: for some clients replace the original pair with curated
          // Evy Baby-style Instagram pair embeds (explicit srcs provided).
          if (client.id === 'istikbal' || client.id === 'bellona') {
            const specialPairs: Record<string, [string, string]> = {
              istikbal: [
                'https://www.instagram.com/reel/DQCPdSGAktu/embed/',
                'https://www.instagram.com/reel/DQe3KP6iHy3/embed/'
              ],
              bellona: [
                'https://www.instagram.com/reel/DCcNTfOOBC5/embed/',
                'https://www.instagram.com/reel/DRhzDFYjM8U/embed/'
              ]
            }
            const [firstSrc, secondSrc] = specialPairs[client.id] ?? [video, next]
            const forceFallbackForPair = client.id === 'bellona' ? false : false
            elements.push(
              <div key={`pair-special-${client.id}-${i}`} className="col-span-full flex justify-center">
                <InstagramPair
                  srcs={[firstSrc, secondSrc]}
                  titles={[`${client.name} video ${i + 1}`, `${client.name} video ${i + 2}`]}
                  forceFallback={forceFallbackForPair}
                />
              </div>
            )
            i++ // skip next because we've replaced the pair
          } else {
            elements.push(
              <div key={`pair-${i}`} className="col-span-full">
                <InstagramPair
                  srcs={[video, next]}
                  titles={[`${client.name} video ${i + 1}`, `${client.name} video ${i + 2}`]}
                  forceFallback={client.id === 'istikbal'}
                />
              </div>
            )
            i++ // skip next because it's rendered in the pair
          }
        } else {
          // Single instagram embed centered
          /**
           * Special-case:
           * - If this single reel is the one shown in the page snippet (DExhItvsUx5),
           *   append the requested post embed so both appear together as live iframes (Evy Baby settings).
           */
          const requestedExtra = 'https://www.instagram.com/reel/DRxMDO2DFwx/embed/'
          const shouldAppendExtra = video.includes('DExhItvsUx5') // match the reel id seen in the snippet
          const srcs = shouldAppendExtra ? [video, requestedExtra] : [video]
          const titles = srcs.map((_, idx) => `${client.name} video ${i + 1}${idx > 0 ? ` (extra ${idx})` : ''}`)

          // Render as live iframes (no forced fallback) using the Evy Baby embed sizing/styling
          elements.push(
            <div key={`single-${i}`} className="col-span-full flex justify-center">
              <InstagramPair srcs={srcs} titles={titles} forceFallback={false} />
            </div>
          )
        }
        continue
      }

      // Non-instagram: render as aspect-video iframe
      elements.push(
        <div key={i} className="aspect-video bg-black rounded-lg overflow-hidden">
          <iframe
            src={video}
            className="w-full h-full"
            title={`${client.name} video ${i + 1}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
          />
        </div>
      )
    }

    // If this client requires the curated Ona pair to be visible under Videos,
    // and the pair isn't already present in the original list, inject the two
    // instagram iframe grid items so they appear like other videos.
    if (client.id === 'ona') {
      const alreadyHasOne = sortedOriginalVideos.some((v) => v.includes('DSANxKviGcM') || v.includes('DQJ0wcIiIZi'))
      if (!alreadyHasOne) {
        // Insert Instagram separator if not already present
        if (firstInstagramIndex === -1) {
          elements.push(
            <div key="sep-ona" className="col-span-full flex items-center my-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="px-3 text-sm text-gray-500">Instagram</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          )
        }

        // Render ONA as Evy Baby–style Instagram pair (tall 350x630 iframes)
        elements.push(
          <div key="ona-instagram-pair" className="col-span-full flex justify-center">
            <InstagramPair
              srcs={[onaPair[0], onaPair[1]]}
              titles={[`${client.name} video (ona) 1`, `${client.name} video (ona) 2`]}
              forceFallback={false}
            />
          </div>
        )
      }
    }

    return elements
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button asChild variant="outline" size="sm" className="bg-transparent">
                <Link to="/portfolio" className="flex items-center">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Portfolio
                </Link>
              </Button>

              <div className="flex items-center space-x-3">
                <img
                  src={client.logo}
                  alt={`${client.name} logo`}
                  className="h-12 w-12 object-contain rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.src =
                      'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
                  }}
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                  <p className="text-gray-600">{client.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Services */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Services Provided</h2>
              <p className="text-gray-600 mb-4">
                {client.services.length > 0 ? 'Services we worked on with this client:' : 'No services listed for this client.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {client.services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Original Media */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Original Media</h2>

              {/* Videos */}
              {sortedOriginalVideos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Videos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    {renderVideoElements()}
                  </div>
                </div>
              )}

              {/* Bellona: Render curated Evy Baby-style Instagram pair embeds */}
              {client.id === 'bellona' && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Instagram</h3>
                  <div className="flex justify-center">
                    <InstagramPair
                      srcs={[
                        'https://www.instagram.com/reel/DCcNTfOOBC5/embed/',
                        'https://www.instagram.com/reel/DRhzDFYjM8U/embed/'
                      ]}
                      titles={[`${client.name} post 1`, `${client.name} post 2`]}
                      forceFallback={false}
                    />
                  </div>
                </div>
              )}

              {/* Images: use OriginalImagesManager so anyone can add/replace images */}
              <div>
                <OriginalImagesManager
                  clientId={clientId ?? 'unknown'}
                  dataImages={client.mediaLinks.images}
                  clientName={client.name}
                />
              </div>
            </div>

            {/* Uploaded Media - Admin only */}
            {isAdmin && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Uploaded Media</h2>

                  {/* Upload Status */}
                  {uploadStatus === 'success' && (
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Upload successful!</span>
                    </div>
                  )}
                  {uploadStatus === 'error' && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Upload failed</span>
                    </div>
                  )}
                </div>

                {/* Hidden inputs for uploads */}
                <div className="flex space-x-4 mb-6">
                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                  <input ref={videoInputRef} type="file" accept="video/*" multiple onChange={handleVideoUpload} className="hidden" />

                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="bg-transparent">
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Upload Images
                  </Button>

                  <Button variant="outline" onClick={() => videoInputRef.current?.click()} disabled={isUploading} className="bg-transparent">
                    <Video className="h-4 w-4 mr-2" />
                    Upload Videos
                  </Button>
                </div>

                {/* Uploaded Videos */}
                {uploadedVideos.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Uploaded Videos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {uploadedVideos.map((video, index) => (
                        <div key={index} className="relative aspect-video bg-black rounded-lg overflow-hidden group">
                          <video src={video} className="w-full h-full object-cover" controls />
                          <button
                            onClick={() => {
                              const updated = uploadedVideos.filter((_, i) => i !== index)
                              setUploadedVideos(updated)
                              if (clientId) localStorage.setItem(`client_${clientId}_videos`, JSON.stringify(updated))
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove uploaded video"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploaded Images */}
                {uploadedImages.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Uploaded Images</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                          <img src={image} alt={`Uploaded image ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => {
                              const updated = uploadedImages.filter((_, i) => i !== index)
                              setUploadedImages(updated)
                              if (clientId) localStorage.setItem(`client_${clientId}_images`, JSON.stringify(updated))
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove uploaded image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadedVideos.length === 0 && uploadedImages.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No uploaded files yet. Use the buttons above to add images or videos.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Client Details</h3>
              <p className="text-gray-600 mb-3">{client.description}</p>

              <div className="flex flex-col space-y-2">
                {isAdmin && (
                  <Link to={`/portfolio/${client.id}`} className="text-sm text-blue-600 hover:underline">
                    Open portfolio route
                  </Link>
                )}
                {mergedSocialLinks.facebook && (
                  <a href={mergedSocialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:underline">
                    Facebook
                  </a>
                )}
                {mergedSocialLinks.instagram && (
                  <a href={mergedSocialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:underline">
                    Instagram
                  </a>
                )}
                {mergedSocialLinks.youtube && (
                  <a href={mergedSocialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:underline">
                    YouTube
                  </a>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Quick Actions</h3>
              <div className="flex flex-col space-y-2">
                <Button asChild variant="outline" className="bg-transparent">
                  <Link to="/portfolio">Back to Portfolio</Link>
                </Button>
                {isAdmin && (
                  <Button
                    variant="outline"
                    className="bg-transparent"
                    onClick={() => {
                      if (clientId) {
                        localStorage.removeItem(`client_${clientId}_images`)
                        localStorage.removeItem(`client_${clientId}_videos`)
                        setUploadedImages([])
                        setUploadedVideos([])
                      }
                    }}
                  >
                    Clear Uploaded Media
                  </Button>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}
